import type {
  DirectoryOrg,
  PaginatedActions,
  ParticipationCounts,
  ParticipationStatus,
  PublicActionDetail,
  PublicOrg,
  PublicRegion,
} from "./types.js";

const HEADERS = { Accept: "application/json" };
const OPTS = { headers: HEADERS, credentials: "omit" } as const;

// Follow cursor pages until `next` is null, concatenating results. Mirrors the
// frontend `fetchBerlinWideActions({ fetchAll })` so the map can render the full
// upcoming set instead of a single page.
async function drainPages(first: PaginatedActions): Promise<PaginatedActions> {
  if (!first.next) return first;
  const results = [...first.results];
  let nextUrl: string | null = first.next;
  while (nextUrl) {
    const page = await fetchNextPage(nextUrl);
    results.push(...page.results);
    nextUrl = page.next;
  }
  return { ...first, results, next: null };
}

export async function fetchActions(
  apiBase: string,
  orgSlug: string,
  params: {
    upcoming?: boolean;
    limit?: number;
    after?: string;
    before?: string;
    fetchAll?: boolean;
  } = {},
): Promise<PaginatedActions> {
  const url = new URL(`${apiBase}/api/orgs/${orgSlug}/actions`);
  if (params.upcoming !== undefined) {
    url.searchParams.set("upcoming", params.upcoming ? "true" : "false");
  }
  if (params.limit) url.searchParams.set("limit", String(params.limit));
  if (params.after) url.searchParams.set("after", params.after);
  if (params.before) url.searchParams.set("before", params.before);
  const response = await fetch(url.toString(), OPTS);
  if (!response.ok) throw new Error(`API ${response.status}`);
  const data = (await response.json()) as PaginatedActions;
  return params.fetchAll ? drainPages(data) : data;
}

export async function fetchActionDetail(
  apiBase: string,
  id: string,
): Promise<PublicActionDetail> {
  const url = new URL(`${apiBase}/api/actions/${id}`);
  const response = await fetch(url.toString(), OPTS);
  if (!response.ok) throw new Error(`API ${response.status}`);
  return (await response.json()) as PublicActionDetail;
}

export async function fetchBerlinWideActions(
  apiBase: string,
  params: { upcoming?: boolean; limit?: number; after?: string; before?: string; region?: string; fetchAll?: boolean } = {},
): Promise<PaginatedActions> {
  const url = new URL(`${apiBase}/api/actions`);
  if (params.upcoming !== undefined) {
    url.searchParams.set("upcoming", params.upcoming ? "true" : "false");
  }
  if (params.limit) url.searchParams.set("limit", String(params.limit));
  if (params.after) url.searchParams.set("after", params.after);
  if (params.before) url.searchParams.set("before", params.before);
  if (params.region) url.searchParams.set("region", params.region);
  const response = await fetch(url.toString(), OPTS);
  if (!response.ok) throw new Error(`API ${response.status}`);
  const data = (await response.json()) as PaginatedActions;
  return params.fetchAll ? drainPages(data) : data;
}

export async function fetchRegions(apiBase: string): Promise<PublicRegion[]> {
  const url = new URL(`${apiBase}/api/regions/`);
  const response = await fetch(url.toString(), OPTS);
  if (!response.ok) throw new Error(`API ${response.status}`);
  return (await response.json()) as PublicRegion[];
}

export async function postParticipation(
  apiBase: string,
  actionId: string,
  body: {
    pseudo_id: string;
    status: ParticipationStatus;
    display_name?: string;
    name_consent?: boolean;
  },
): Promise<{ status: ParticipationStatus; participation: ParticipationCounts }> {
  const response = await fetch(`${apiBase}/api/actions/${actionId}/participation`, {
    method: "POST",
    headers: { ...HEADERS, "Content-Type": "application/json" },
    credentials: "omit",
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`API ${response.status}`);
  return (await response.json()) as { status: ParticipationStatus; participation: ParticipationCounts };
}

export async function fetchOrg(apiBase: string, slug: string): Promise<PublicOrg> {
  const url = new URL(`${apiBase}/api/orgs/${slug}`);
  const response = await fetch(url.toString(), OPTS);
  if (!response.ok) throw new Error(`API ${response.status}`);
  return (await response.json()) as PublicOrg;
}

export async function fetchDirectory(apiBase: string): Promise<DirectoryOrg[]> {
  const url = new URL(`${apiBase}/api/orgs/`);
  url.searchParams.set("include", "next_actions");
  const response = await fetch(url.toString(), OPTS);
  if (!response.ok) throw new Error(`API ${response.status}`);
  return (await response.json()) as DirectoryOrg[];
}

export async function fetchNextPage(nextUrl: string): Promise<PaginatedActions> {
  const response = await fetch(nextUrl, OPTS);
  if (!response.ok) throw new Error(`API ${response.status}`);
  return (await response.json()) as PaginatedActions;
}
